/**
 * Renders a T&C body — markdown or plain prose — as React elements.
 *
 * The markdown parser lives in the contracts package, which has no React
 * dependency, so it hands back an AST rather than HTML and each front end
 * turns that into elements. That is the point, not an inconvenience: nothing
 * here calls `dangerouslySetInnerHTML`, so operator-authored text cannot
 * become markup no matter what the parser does. The Outlook copy is the one
 * surface that needs an HTML string, and it uses the emitter instead.
 *
 * `format: 'text'` renders exactly what this component's predecessor did —
 * one `pre-wrap` block — so a template that has not opted into markdown looks
 * the same as it always has.
 */

import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  parseRestrictedMarkdown,
  type BlockNode,
  type InlineNode,
  type TermsBodyFormat,
} from '@techgitter/contracts';

export interface TermsMarkdownProps {
  body: string | null | undefined;
  format: TermsBodyFormat;
  /** Applied to the wrapper — callers set the type scale and colour. */
  sx?: SxProps<Theme>;
}

const renderInline = (nodes: InlineNode[]): React.ReactNode =>
  nodes.map((node, index) => {
    switch (node.type) {
      case 'text':
        return <React.Fragment key={index}>{node.value}</React.Fragment>;
      case 'break':
        return <br key={index} />;
      case 'strong':
        return <strong key={index}>{renderInline(node.children)}</strong>;
      case 'em':
        return <em key={index}>{renderInline(node.children)}</em>;
      case 'link':
        // The parser has already refused anything but http/https/mailto, so a
        // node reaching here carries a target worth rendering.
        return (
          <Link
            key={index}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
          >
            {renderInline(node.children)}
          </Link>
        );
      default:
        return null;
    }
  });

const HEADING_SX: Record<1 | 2 | 3, SxProps<Theme>> = {
  1: { fontSize: '1.25em', fontWeight: 600, mt: 2, mb: 1 },
  2: { fontSize: '1.12em', fontWeight: 600, mt: 1.75, mb: 0.75 },
  3: { fontSize: '1em', fontWeight: 600, mt: 1.5, mb: 0.5 },
};

const renderBlock = (block: BlockNode, key: number): React.ReactNode => {
  switch (block.type) {
    case 'paragraph':
      return (
        <Typography key={key} component="p" sx={{ m: 0, mb: 1.5, fontSize: 'inherit' }}>
          {renderInline(block.children)}
        </Typography>
      );
    case 'heading':
      return (
        <Typography
          key={key}
          component="div"
          sx={{ ...HEADING_SX[block.level], fontSize: 'inherit' }}
        >
          {renderInline(block.children)}
        </Typography>
      );
    case 'list':
      return (
        <Box
          key={key}
          component={block.ordered ? 'ol' : 'ul'}
          sx={{ m: 0, mb: 1.5, pl: 2.75 }}
        >
          {block.items.map((item, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {renderInline(item)}
            </li>
          ))}
        </Box>
      );
    case 'blockquote':
      return (
        <Box
          key={key}
          sx={{
            m: 0,
            mb: 1.5,
            pl: 1.5,
            borderLeft: '3px solid #e2eaf3',
            color: '#555',
          }}
        >
          {block.children.map(renderBlock)}
        </Box>
      );
    case 'rule':
      return (
        <Box
          key={key}
          component="hr"
          sx={{ border: 0, borderTop: '1px solid #e2eaf3', my: 2 }}
        />
      );
    default:
      return null;
  }
};

export const TermsMarkdown: React.FC<TermsMarkdownProps> = ({
  body,
  format,
  sx,
}) => {
  if (!body) return null;

  if (format !== 'markdown') {
    return (
      <Typography component="div" sx={{ whiteSpace: 'pre-wrap', ...sx }}>
        {body}
      </Typography>
    );
  }

  return (
    // The trailing block's margin is trimmed so the body sits flush against
    // whatever card it lands in, the way the pre-wrap version did.
    <Box sx={{ '& > :last-child': { mb: 0 }, ...sx }}>
      {parseRestrictedMarkdown(body).map(renderBlock)}
    </Box>
  );
};
